import { Grid, Menu, Paper, Table, ButtonGroup, Loader, Alert, MultipleAutocomplete, MultipleAutocompleteOption, DatePicker, Button, Modal } from "../../../components";
import moment from "moment";
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Chip, Link } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { formatDate, formatMoney, getPaymentStatusColor, getPaymentStatusLabel } from "../../../utils";
import { useAxios, useToggle } from "../../../hooks";
import { Fragment, useMemo, useRef, useState } from "react";
import { sum } from "lodash";
import { GridFilterInputValueProps, GridFilterItem } from "@mui/x-data-grid-pro";
import {  GridFilterOperator } from "@mui/x-data-grid-pro";
import { enqueueSnackbar } from "notistack";

const today = moment().toDate()
const oneMonth = moment().add(30, 'days').toDate()

const diff = moment(oneMonth).diff(today, 'days')

const month: Array<Date> = []

for (let day = 0; day < diff; day++) {
    month.push(moment(today).add(day, 'day').toDate())
}

const statusOptions = [
    { value: 'AWAITING_ORDER_CONFIRMATION', label: 'Aguardando Confirmação' },
    { value: 'CANCELLED', label: 'Cancelado' },
    { value: 'PENDING', label: 'Pendente' },
    { value: 'PAID', label: 'Pago' },
    { value: 'LATE', label: 'Atrasado' },
]

function StatusSelector(props: GridFilterInputValueProps) {
    const { item, applyValue } = props;
  
    return (
        <MultipleAutocomplete
        variant="standard"
            label="Status"
            options={statusOptions}
            value={statusOptions.filter(option => item?.value?.includes(option.value))}
            onChange={(value) => {
                applyValue({
                    ...item,
                    value: (value as unknown as MultipleAutocompleteOption[]).map(item => item.value)
                })
            }}
        />
    );
  }
  
const statusSelectorOperator: GridFilterOperator[] = [
{
    label: 'é igual a',
    value: 'in',
    getValueAsString: (values) => {
        return values.map((item: string) => statusOptions.find(({value}) => value === item)?.label).join(', ')
    },
    getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (!Array.isArray(filterItem.value) || filterItem.value.length === 0 || !filterItem.value) {
            return null;
        }

        return ({ value }) => {
            return filterItem.value.includes(value)
        };
    },
    InputComponent: StatusSelector,
}]; 

export function Payments () {
    const navigate = useNavigate()

    const [confirmModalVisible, setConfirmModalVisibility] = useToggle(false)
    const confirmModalAction = useRef<null | (() => Promise<void>)>(null)

    const [rowSelectionModel, setRowSelectionModel] = useState<Array<string | number>>([]);
    const [payments, refetchPayments] = useAxios(`/payment`)
    const [updatedPayment, updatePayment] = useAxios({
        url: `/payment`,
        method: 'POST'
    }, {
        manual: true
    })
    const [api, callAPI] = useAxios({}, {manual: true})

    const dash = useMemo(() => {
        return month.map(item => {
            const items = (payments.data || [])
                ?.filter((payment: { due_date: Date }) => moment(payment.due_date).isSame(item, 'date'))
                ?.filter((payment: { status: string }) => payment.status !== 'PAID' && payment.status !== 'CANCELLED')
                ?.map((item: { amount: string }) => Number(item.amount))

            return {
                due_date: formatDate(item),
                amount: sum(items)
            }
        })
    }, [payments.data])

    return (
        <Grid container justifyContent="center" /*minHeight={"calc(100vh - 64px)"}*/ padding={3} gap={3} >
            {(payments.loading) ? (
                <div style={{
                    display: 'flex',
                    flex: '1',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <Loader />
                </div>
            ) :(
            <Fragment> 
                {confirmModalAction.current && confirmModalVisible && (
                <Modal
                    title="Atenção!"
                    open={confirmModalVisible}
                    onConfirm={confirmModalAction.current}
                    confirmLabel="Confirmar"
                    onClose={() => {
                        setConfirmModalVisibility.setFalse()
                        confirmModalAction.current = null
                    }}
                >
                    <div>
                        Esta ação é irreversível e não pode ser desfeita.<br/>
                        Você tem certeza que deseja <span style={{fontWeight: 'bold'}}>CONFIRMAR</span> este(s) pagamento(s)?
                    </div>
                </Modal>)}
                <Grid item xs={12} sm={10}>
                        <Alert color="info" icon={false}>
                            Esta listagem inicialmente apenas mostra pagamentos com status <span style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>Atrasado</span> e <span style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>Pendente</span>.<br/>Para ver todos os registros, limpe os filtros da tabela.
                        </Alert>
                    </Grid>
                <Grid xs={12} sm={10}> 
                    <Alert 
                        icon={false}          
                    >
                        <strong>Próximos Pagamentos:</strong>
                        <ul
                            style={{
                                columnCount: 3,
                                width: '50vw'
                            }}
                        >
                            {
                                dash.map(item => {
                                    return (
                                        <li>
                                            <strong>{item.due_date}</strong>: {formatMoney(item.amount)}
                                        </li>
                                    )
                                })
                            }
                        </ul>
                    </Alert>
                </Grid>
                {rowSelectionModel.length ? (<Grid item xs={12} sm={10} textAlign="left">
                    <Button
                        onClick={() => {
                            confirmModalAction.current = async () => {
                                await callAPI({
                                    url: `/payment/batch`,
                                    method: 'PUT',
                                    data: {
                                        ids: rowSelectionModel,
                                        status: 'PAID'
                                    }
                                })

                                refetchPayments()
                                setConfirmModalVisibility.setFalse()
                                setRowSelectionModel([])
                            }

                            setConfirmModalVisibility.setTrue()
                        }}
                    >
                        Confirmar
                    </Button>
                </Grid>) : null}
                <Grid item xs={12} sm={10}>
                    <Paper elevation={1}>
                    <Table 
                        rowSelection={true}
                        checkboxSelection
                        disableRowSelectionOnClick
                        onRowSelectionModelChange={(newRowSelectionModel) => {
                            setRowSelectionModel(newRowSelectionModel);
                        }}
                        rowSelectionModel={rowSelectionModel}
                        isRowSelectable={({ row }) => (row.status === 'LATE' || row.status === 'PENDING')}
                        rowHeight={65}
                        initialState={{
                            filter: {
                                filterModel: {
                                    items: [
                                        { 
                                            field: 'status',
                                            operator: 'in',
                                            value: ['PENDING', 'LATE']
                                        }
                                    ]
                                }
                            }
                        }}
                        columns={[
                            { field: 'id', headerName: 'ID', width: 100 },
                            { field: 'created_at', headerName: 'Data', width: 200, valueFormatter: ({value}) => {
                                return moment(value).format('DD/MM/YYYY HH:mm:ss')
                            } },
                            { field: 'due_date', headerName: 'Vencimento', width: 200, valueFormatter: ({value}) => {
                                return moment(value).format('DD/MM/YYYY HH:mm:ss')
                            }, renderCell: ({value, row}) => {
                                return (
                                    <DatePicker 
                                        disabled={row.status === 'PAID' || row.status === 'CANCELLED'}
                                        onChange={async (newValue) => {
                                            const {status} = await  updatePayment({
                                                url: `/payment/${row.id}`,
                                                method: 'POST',
                                                data: {
                                                    due_date: newValue
                                                }
                                            })

                                            if (status === 204) {
                                                enqueueSnackbar('Data de vencimento atualizada com sucesso!', {
                                                    variant: 'success'
                                                })
                                            }
                                        }}
                                        value={moment(value)}
                                    />
                                )
                            } },
                            { field: 'order_id', headerName: 'Emissão', width: 120, type: 'string', renderCell: ({value}) => {
                                return (
                                    <Link href={`/admin/emissoes/${value}`}>
                                        # {value}
                                    </Link>
                                )
                            }},
                            {
                                field: 'supplier_name', headerName: 'Fornecedor', flex: 1
                            },
                            { field: 'amount', headerName: 'Valor', width: 120, valueFormatter: ({value }) => {
                                return formatMoney(value)
                            }},
                            {
                                field: 'status', headerName: 'Status', flex: 1, 
                                valueFormatter: ({value}) => {
                                    return getPaymentStatusLabel(value)
                                },
                                renderCell: ({ formattedValue, value }) => {
                                    return (
                                        <Chip color={getPaymentStatusColor(value)} label={formattedValue} />
                                    )
                                },
                                filterOperators: statusSelectorOperator
                            },
                            {
                                field: 'actions', headerName: '', sortable: false, filterable: false, disableColumnMenu: true, renderCell: ({row}) => {
                                    return (
                                        <ButtonGroup>
                                            <Menu
                                                disabled={row.status === 'AWAITING_ORDER_CONFIRMATION'}
                                                options={[
                                                    { 
                                                        label: 'Editar Pagamento', 
                                                        onClick: async () => {
                                                            navigate(`/admin/pagamentos/${row.id}`)
                                                        }
                                                    }
                                                ]}
                                            >
                                                <MoreHorizIcon />
                                            </Menu>
                                        </ButtonGroup>
                                    )
                                }
                            }
                        ]}
                        rows={(payments?.data || []).map((item: { order: { bid: { user: { name: string }}}}) => ({
                            ...item,
                            supplier_name: item?.order?.bid?.user?.name
                        }))}
                    />
                    </Paper>
                </Grid>
            </Fragment>
            )}
        </Grid>
    )
}